<div class="voucher-revoke-container">
    <!-- Header -->
    <div class="page-header">
        <div class="header-content">
            <h1 class="page-title">
                <i class="fas fa-undo-alt"></i>
                Revoke Voucher
            </h1>
            <p class="page-subtitle">Search for customer and revoke redeemed vouchers</p>
        </div>
    </div>

    <!-- Customer Search Section (shown when no customer is selected) -->
    <div *ngIf="!selectedCustomer" class="search-section">
        <div class="search-card">
            <div class="search-header">
                <i class="fas fa-search"></i>
                <h2>Search Customer</h2>
            </div>

            <div class="search-input-wrapper">
                <input #searchInput type="text" [(ngModel)]="searchTerm" (ngModelChange)="onSearchChange($event)"
                    placeholder="Search by phone number or name..." class="search-input" />
                <i class="fas fa-search search-icon"></i>
            </div>

            <!-- Loading State -->
            <div *ngIf="isSearching" class="loading-state">
                <div class="spinner"></div>
                <p>Searching customers...</p>
            </div>

            <!-- Error Message -->
            <div *ngIf="searchErrorMessage" class="error-message">
                <i class="fas fa-exclamation-circle"></i>
                {{ searchErrorMessage }}
            </div>

            <!-- Customer Results -->
            <div *ngIf="!isSearching && customers.length > 0" class="customer-results">
                <div class="results-header">
                    <span>{{ totalCount }} customer(s) found</span>
                </div>

                <div class="customer-list">
                    <div *ngFor="let customer of customers" class="customer-item" (click)="selectCustomer(customer)">
                        <div class="customer-info">
                            <div class="customer-name">
                                <i class="fas fa-user"></i>
                                {{ customer.name }}
                            </div>
                            <div class="customer-phone">
                                <i class="fas fa-phone"></i>
                                {{ customer.phoneNumber }}
                            </div>
                        </div>
                        <i class="fas fa-chevron-right"></i>
                    </div>
                </div>

                <!-- Load More Button -->
                <button *ngIf="hasMoreResults" (click)="loadMore()" class="load-more-btn" [disabled]="isSearching">
                    <i class="fas fa-arrow-down"></i>
                    Load More
                </button>
            </div>

            <!-- No Results -->
            <div *ngIf="!isSearching && hasSearched && customers.length === 0 && searchTerm.length >= 2"
                class="no-results">
                <i class="fas fa-user-slash"></i>
                <p>No customers found</p>
                <span>Try searching with a different phone number or name</span>
            </div>

            <!-- Search Hint -->
            <div *ngIf="!hasSearched && searchTerm.length < 2" class="search-hint">
                <i class="fas fa-info-circle"></i>
                <p>Enter at least 2 characters to search</p>
            </div>
        </div>
    </div>

    <!-- Voucher Revoke Section (shown when customer is selected) -->
    <div *ngIf="selectedCustomer" class="revoke-section">
        <!-- Selected Customer Info -->
        <div class="selected-customer-card">
            <button (click)="clearCustomer()" class="back-btn">
                <i class="fas fa-arrow-left"></i>
                Back to Search
            </button>

            <div class="customer-details">
                <div class="customer-avatar">
                    <i class="fas fa-user"></i>
                </div>
                <div class="customer-info-detail">
                    <h3>{{ selectedCustomer.name }}</h3>
                    <p>{{ selectedCustomer.phoneNumber }}</p>
                </div>
            </div>
        </div>

        <!-- Redemption Reference Search -->
        <div class="reference-search-card">
            <div class="card-header">
                <i class="fas fa-receipt"></i>
                <h3>Search by Redemption Reference</h3>
            </div>

            <div class="reference-input-group">
                <input type="text" [(ngModel)]="redemptionReference" placeholder="Enter redemption reference..."
                    class="reference-input" (keyup.enter)="searchVouchersByReference()" />
                <button (click)="searchVouchersByReference()" class="search-btn"
                    [disabled]="isLoadingVouchers || !redemptionReference.trim()">
                    <i class="fas fa-search"></i>
                    Search
                </button>
            </div>

            <!-- Error Message -->
            <div *ngIf="vouchersErrorMessage" class="error-message">
                <i class="fas fa-exclamation-circle"></i>
                {{ vouchersErrorMessage }}
            </div>
        </div>

        <!-- Vouchers List -->
        <div *ngIf="vouchers.length > 0" class="vouchers-section">
            <div class="vouchers-header">
                <h3>
                    <i class="fas fa-ticket-alt"></i>
                    Found {{ vouchers.length }} voucher(s)
                </h3>
            </div>

            <div class="vouchers-list">
                <div *ngFor="let voucher of vouchers" class="voucher-card"
                    [class.selected]="isVoucherSelected(voucher.id!)" [class.revokable]="canRevokeVoucher(voucher)"
                    [class.not-revokable]="!canRevokeVoucher(voucher)">

                    <div class="voucher-header-row">
                        <div class="voucher-code">
                            <i class="fas fa-barcode"></i>
                            {{ voucher.voucherCode }}
                        </div>
                        <span class="voucher-status" [ngClass]="getStatusClass(voucher.status)">
                            {{ getStatusLabel(voucher.status) }}
                        </span>
                    </div>

                    <div class="voucher-details-grid">
                        <div class="detail-item">
                            <span class="detail-label">Amount</span>
                            <span class="detail-value amount">₹{{ voucher.voucherAmount }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Partner</span>
                            <span class="detail-value">{{ voucher.partnerName }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Redeemed At</span>
                            <span class="detail-value">{{ formatDateTime(voucher.redeemedAt) }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Redemption Ref</span>
                            <span class="detail-value">{{ voucher.redemptionReference || 'N/A' }}</span>
                        </div>
                    </div>

                    <div *ngIf="voucher.notes" class="voucher-notes">
                        <i class="fas fa-sticky-note"></i>
                        <span>{{ voucher.notes }}</span>
                    </div>

                    <div *ngIf="!canRevokeVoucher(voucher)" class="warning-message">
                        <i class="fas fa-info-circle"></i>
                        Only redeemed vouchers can be revoked
                    </div>
                </div>
            </div>

            <!-- Revoke Action Section -->
            <div *ngIf="hasRevokableVouchers()" class="revoke-action-card">
                <div class="card-header">
                    <i class="fas fa-undo"></i>
                    <h3>Revoke Vouchers</h3>
                </div>

                <!-- OTP Verification Section -->
                <div *ngIf="showOtpSection" class="otp-section" [@slideDown]>
                    <div class="otp-section-header">
                        <h4>Verify Customer Identity</h4>
                        <button class="close-btn" (click)="closeOtpSection()" [disabled]="isRevoking">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <div class="otp-section-body">
                        <p class="otp-info">
                            <i class="fas fa-info-circle"></i>
                            An OTP will be sent to the customer's phone number for verification
                        </p>

                        <app-otp-verification [phoneNumber]="selectedCustomer!.phoneNumber" [autoSend]="true"
                            (verified)="onOtpVerified()" (error)="onOtpError($event)">
                        </app-otp-verification>

                        <!-- Additional Fields (shown after OTP verification) -->
                        <div *ngIf="otpVerified" class="additional-fields" [@slideDown]>
                            <div class="form-group">
                                <label for="revokeNotes">Notes (Optional)</label>
                                <textarea id="revokeNotes" [(ngModel)]="revokeNotes"
                                    placeholder="Enter reason for revocation..." rows="3" class="notes-textarea"
                                    [disabled]="isRevoking"></textarea>
                            </div>

                            <!-- Revoke Button -->
                            <button class="btn-revoke-confirm" (click)="revokeVoucher()" [disabled]="isRevoking">
                                <i class="fas fa-undo" *ngIf="!isRevoking"></i>
                                <i class="fas fa-spinner fa-spin" *ngIf="isRevoking"></i>
                                <span>{{ isRevoking ? 'Revoking Vouchers...' : 'Revoke Vouchers' }}</span>
                            </button>

                            <!-- Processing Message -->
                            <div class="processing-message" *ngIf="isRevoking">
                                <div class="spinner-small"></div>
                                <span>Processing revocation...</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Initial Form (shown when OTP section is not visible) -->
                <div *ngIf="!showOtpSection">
                    <div class="form-group">
                        <label for="revokeNotes">Notes (Optional)</label>
                        <textarea id="revokeNotes" [(ngModel)]="revokeNotes"
                            placeholder="Enter reason for revocation..." rows="3" class="notes-textarea"></textarea>
                    </div>

                    <button (click)="initiateRevoke()" class="revoke-btn">
                        <i class="fas fa-undo"></i>
                        Proceed to Verify & Revoke
                    </button>
                </div>

                <!-- Success Message -->
                <div *ngIf="revokeSuccessMessage" class="success-message">
                    <i class="fas fa-check-circle"></i>
                    {{ revokeSuccessMessage }}
                </div>

                <!-- Error Message -->
                <div *ngIf="revokeErrorMessage" class="error-message">
                    <i class="fas fa-exclamation-circle"></i>
                    {{ revokeErrorMessage }}
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingVouchers" class="loading-state">
            <div class="spinner"></div>
            <p>Loading vouchers...</p>
        </div>
    </div>
</div>